/**
 * RFC-114 LoR5 — Discipline Toggle
 *
 * Horizontal toggle buttons for discipline-layer filtering at LoR5.
 * Renders when activeLoR >= 4 (toolbar appears as soon as you enter a building).
 *
 * Architecture reference: MULTI_SCALE_LOR_ARCHITECTURE.md §7.2 (Discipline Filtering UX)
 */
import type { CSSProperties } from 'react';
import { tokens } from '@/design';
import { type DisciplineId, ALL_DISCIPLINES, DISCIPLINE_LABELS } from '@/engines/giro3d/lorScopeManager';

export interface DisciplineToggleProps {
  /** Current active LoR (renders only when >= 4). */
  readonly activeLoR: number;
  /** Set of currently-enabled disciplines. Empty = all shown. */
  readonly activeDisciplines: Set<DisciplineId>;
  /** Disciplines that have geometry available (others are dimmed). */
  readonly availableDisciplines?: Set<DisciplineId>;
  /** Callback when a discipline button is clicked. */
  readonly onToggle: (discipline: DisciplineId) => void;
}

export function DisciplineToggle({
  activeLoR,
  activeDisciplines,
  availableDisciplines,
  onToggle,
}: DisciplineToggleProps) {
  if (activeLoR < 4) return null;

  const allActive = activeDisciplines.size === 0;

  return (
    <div
      style={containerStyle}
      role="toolbar"
      aria-label="Discipline toggle"
      data-testid="lor-discipline-toggle"
    >
      {ALL_DISCIPLINES.map((disc) => {
        const isActive = allActive || activeDisciplines.has(disc);
        const hasGeometry = !availableDisciplines || availableDisciplines.has(disc);
        return (
          <DisciplineButton
            key={disc}
            id={disc}
            label={DISCIPLINE_LABELS[disc]}
            active={isActive}
            hasGeometry={hasGeometry}
            onClick={() => onToggle(disc)}
          />
        );
      })}
    </div>
  );
}

interface DisciplineButtonProps {
  readonly id: DisciplineId;
  readonly label: string;
  readonly active: boolean;
  readonly hasGeometry: boolean;
  readonly onClick: () => void;
}

function DisciplineButton({ id, label, active, hasGeometry, onClick }: DisciplineButtonProps) {
  const style = active
    ? hasGeometry
      ? activeButtonStyle
      : activeNoGeomButtonStyle
    : inactiveButtonStyle;

  return (
    <button
      type="button"
      style={style}
      onClick={onClick}
      aria-label={`Toggle ${label} discipline`}
      aria-pressed={active}
      title={hasGeometry ? label : `${label} (no geometry)`}
      data-testid={`lor-discipline-${id}`}
    >
      {label}
      {active && <span style={checkStyle} aria-hidden="true">✓</span>}
    </button>
  );
}

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  pointerEvents: 'auto',
};

const buttonBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 3,
  height: 26,
  paddingInline: 8,
  borderRadius: tokens.radius.sm,
  border: 'none',
  cursor: 'pointer',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.03em',
  transition: `background ${tokens.motion.duration.fast}ms ease, opacity ${tokens.motion.duration.fast}ms ease`,
  whiteSpace: 'nowrap',
};

const activeButtonStyle: CSSProperties = {
  ...buttonBase,
  background: tokens.color.accent.primary,
  color: tokens.color.accent.on,
  opacity: 1,
};

const activeNoGeomButtonStyle: CSSProperties = {
  ...buttonBase,
  background: tokens.color.surface[3],
  color: tokens.color.ink.secondary,
  opacity: 0.6,
  cursor: 'default',
};

const inactiveButtonStyle: CSSProperties = {
  ...buttonBase,
  background: tokens.color.surface[2],
  color: tokens.color.ink.muted,
  opacity: 0.7,
};

const checkStyle: CSSProperties = {
  fontSize: 9,
};
